<template>
  <div>
    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
    </div>

    <!-- No results yet -->
    <div v-else-if="!occurrence" class="text-center py-12">
      <h3 class="text-lg font-heading font-semibold text-gray-900 dark:text-gray-100 mb-2">
        Keyword Counting
      </h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Count keyword occurrences across your page and competitors to find gaps and over-optimization.
      </p>
      <button @click="handleRun" class="btn btn-primary">
        Run Counting
      </button>
    </div>

    <!-- Running -->
    <div v-else-if="occurrence.status !== 'completed' && occurrence.status !== 'failed'" class="text-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4" />
      <p class="text-sm text-gray-600 dark:text-gray-400">
        {{ progressMessage || 'Counting keywords...' }}
      </p>
    </div>

    <!-- Failed -->
    <div v-else-if="occurrence.status === 'failed'" class="text-center py-12">
      <p class="text-sm text-red-600 dark:text-red-400 mb-4">
        {{ occurrence.error_message || 'Counting failed. Please try again.' }}
      </p>
      <button @click="handleRun" class="btn btn-primary">
        Retry
      </button>
    </div>

    <!-- Results -->
    <div v-else class="space-y-6">
      <!-- 1. Delta Summary Card -->
      <div class="grid grid-cols-3 gap-4">
        <div class="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-center">
          <div class="text-2xl font-bold text-red-600 dark:text-red-400">
            {{ criticalCount }}
          </div>
          <div class="text-xs text-red-500 dark:text-red-400 mt-1">
            Critical Gaps
          </div>
        </div>
        <div class="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-center">
          <div class="text-2xl font-bold text-amber-600 dark:text-amber-400">
            {{ gapCount }}
          </div>
          <div class="text-xs text-amber-500 dark:text-amber-400 mt-1">
            Placement Gaps
          </div>
        </div>
        <div class="rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 p-4 text-center">
          <div class="text-2xl font-bold text-primary-600 dark:text-primary-400">
            {{ tfidfNoteCount }}
          </div>
          <div class="text-xs text-primary-500 dark:text-primary-400 mt-1">
            TF-IDF Notes
          </div>
        </div>
      </div>

      <!-- 2. Heading Deltas Table -->
      <div v-if="headingDeltas.length" class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <button @click="expandedSections.headings = !expandedSections.headings" class="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800">
          <span class="font-medium text-gray-900 dark:text-gray-100">
            Heading Deltas ({{ headingDeltas.length }})
          </span>
          <span class="text-gray-400">{{ expandedSections.headings ? '−' : '+' }}</span>
        </button>
        <div v-if="expandedSections.headings" class="p-4 overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-900/50">
                <th class="pb-2 pr-4">Keyword</th>
                <th class="pb-2 pr-4">H1 Status</th>
                <th class="pb-2 pr-4">H2 (Client / Avg)</th>
                <th class="pb-2 pr-4">H3 (Client / Avg)</th>
                <th class="pb-2">Priority</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="delta in headingDeltas" :key="delta.keyword" class="border-t border-gray-100 dark:border-gray-800">
                <td class="py-2 pr-4 text-gray-900 dark:text-gray-100">
                  {{ delta.keyword }}
                </td>
                <td class="py-2 pr-4">
                  <span :class="h1StatusClass(delta.h1?.status)" class="px-2 py-0.5 rounded-lg text-xs font-medium">
                    {{ delta.h1?.status || '—' }}
                  </span>
                </td>
                <td class="py-2 pr-4 text-gray-600 dark:text-gray-400">
                  {{ delta.h2?.client || 0 }} / {{ delta.h2?.competitor_avg || 0 }}
                </td>
                <td class="py-2 pr-4 text-gray-600 dark:text-gray-400">
                  {{ delta.h3?.client || 0 }} / {{ delta.h3?.competitor_avg || 0 }}
                </td>
                <td class="py-2">
                  <span :class="priorityBadgeClass(delta.priority)" class="px-2 py-0.5 rounded-lg text-xs font-medium">
                    {{ delta.priority }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 3. Body Delta Table -->
      <div v-if="bodyDeltas.length" class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <button @click="expandedSections.body = !expandedSections.body" class="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800">
          <span class="font-medium text-gray-900 dark:text-gray-100">
            Body Deltas ({{ bodyDeltas.length }})
          </span>
          <span class="text-gray-400">{{ expandedSections.body ? '−' : '+' }}</span>
        </button>
        <div v-if="expandedSections.body" class="p-4 overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-900/50">
                <th class="pb-2 pr-4">
                  Keyword
                </th>
                <th class="pb-2 pr-4">
                  Current
                </th>
                <th class="pb-2 pr-4">
                  Target
                </th>
                <th class="pb-2 pr-4">
                  Delta
                </th>
                <th class="pb-2">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="delta in bodyDeltas" :key="delta.keyword" class="border-t border-gray-100 dark:border-gray-800">
                <td class="py-2 pr-4 text-gray-900 dark:text-gray-100">
                  {{ delta.keyword }}
                </td>
                <td class="py-2 pr-4 text-gray-600 dark:text-gray-400">
                  {{ delta.client_count }}
                </td>
                <td class="py-2 pr-4 text-gray-600 dark:text-gray-400">
                  {{ delta.target }}
                </td>
                <td class="py-2 pr-4" :class="deltaTextClass(delta.delta)">
                  {{ delta.delta > 0 ? `+${delta.delta}` : delta.delta }}
                </td>
                <td class="py-2">
                  <span :class="statusBadgeClass(delta.status)" class="px-2 py-0.5 rounded-lg text-xs font-medium">
                    {{ delta.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 4. Placement Gaps -->
      <div v-if="placementGaps.length" class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <button @click="expandedSections.gaps = !expandedSections.gaps" class="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800">
          <span class="font-medium text-gray-900 dark:text-gray-100">
            Placement Gaps ({{ placementGaps.length }})
          </span>
          <span class="text-gray-400">{{ expandedSections.gaps ? '−' : '+' }}</span>
        </button>
        <div v-if="expandedSections.gaps" class="p-4 overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-900/50">
                <th class="pb-2 pr-4">
                  Keyword
                </th>
                <th class="pb-2 pr-4">
                  Missing From
                </th>
                <th class="pb-2">
                  Competitor Avg
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="gap in placementGaps" :key="gap.keyword" class="border-t border-gray-100 dark:border-gray-800">
                <td class="py-2 pr-4 text-gray-900 dark:text-gray-100">
                  {{ gap.keyword }}
                </td>
                <td class="py-2 pr-4 text-gray-600 dark:text-gray-400">
                  {{ gap.missing_from }}
                </td>
                <td class="py-2 text-gray-600 dark:text-gray-400">
                  {{ gap.competitor_avg }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 5. TF-IDF Notes -->
      <div v-if="tfidfNotes.length" class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <button @click="expandedSections.tfidf = !expandedSections.tfidf" class="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800">
          <span class="font-medium text-gray-900 dark:text-gray-100">
            TF-IDF Notes ({{ tfidfNotes.length }})
          </span>
          <span class="text-gray-400">{{ expandedSections.tfidf ? '−' : '+' }}</span>
        </button>
        <div v-if="expandedSections.tfidf" class="p-4 space-y-2">
          <div v-for="(note, i) in tfidfNotes" :key="i" class="flex items-start gap-2 text-sm">
            <span class="text-primary-500 mt-0.5">*</span>
            <span class="text-gray-700 dark:text-gray-300">{{ note }}</span>
          </div>
        </div>
      </div>

      <!-- 6. Master Occurrence Table -->
      <div class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <button @click="expandedSections.master = !expandedSections.master" class="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800">
          <span class="font-medium text-gray-900 dark:text-gray-100">
            Master Occurrence Table
          </span>
          <span class="text-gray-400">{{ expandedSections.master ? '−' : '+' }}</span>
        </button>
        <div v-if="expandedSections.master" class="p-4 overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-900/50">
                <th class="pb-2 pr-4">Keyword</th>
                <th class="pb-2 pr-4">Tier</th>
                <th class="pb-2 pr-4">Bucket</th>
                <th class="pb-2 pr-4">Total</th>
                <th class="pb-2 pr-4">Avg</th>
                <th class="pb-2">Target</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in masterTable" :key="row.keyword" class="border-t border-gray-100 dark:border-gray-800">
                <td class="py-2 pr-4 text-gray-900 dark:text-gray-100">
                  {{ row.keyword }}
                </td>
                <td class="py-2 pr-4 text-gray-600 dark:text-gray-400">
                  {{ row.tier }}
                </td>
                <td class="py-2 pr-4 text-gray-600 dark:text-gray-400">
                  {{ row.bucket }}
                </td>
                <td class="py-2 pr-4 text-gray-600 dark:text-gray-400">
                  {{ row.sum }}
                </td>
                <td class="py-2 pr-4 text-gray-600 dark:text-gray-400">
                  {{ row.raw_avg }}
                </td>
                <td class="py-2 text-gray-600 dark:text-gray-400">
                  {{ row.target }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 7. Verification Checklist -->
      <div v-if="verificationChecklist.length" class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <button @click="expandedSections.verification = !expandedSections.verification" class="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800">
          <span class="font-medium text-gray-900 dark:text-gray-100">
            Verification Checklist
          </span>
          <span class="text-gray-400">{{ expandedSections.verification ? '−' : '+' }}</span>
        </button>
        <div v-if="expandedSections.verification" class="p-4 space-y-2">
          <div v-for="(check, i) in verificationChecklist" :key="i" class="flex items-center gap-2 text-sm">
            <span v-if="check.passed" class="text-green-500">&#10003;</span>
            <span v-else class="text-red-500">&#10007;</span>
            <span class="text-gray-700 dark:text-gray-300">{{ check.label }}</span>
          </div>
        </div>
      </div>

      <!-- Over-optimization warnings -->
      <div v-if="overOptimized.length" class="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
        <h4 class="text-sm font-medium text-red-800 dark:text-red-300 mb-3">
          Over-Optimization Detected ({{ overOptimized.length }})
        </h4>
        <div v-for="item in overOptimized" :key="item.keyword" class="flex items-center justify-between py-2 border-t border-red-100 dark:border-red-900 first:border-0">
          <div>
            <span class="text-sm text-gray-900 dark:text-gray-100">{{ item.keyword }}</span>
            <p class="text-xs text-red-600 dark:text-red-400 mt-0.5">
              <template v-if="item.type === 'high_density'">
                Density: {{ item.density }}% on {{ item.domain }} (threshold: 3%)
              </template>
              <template v-else-if="item.type === 'outlier'">
                Count: {{ item.current }} | Competitor avg: {{ item.avg_others }} ({{ item.domain }})
              </template>
              <template v-else>
                Current: {{ item.current }} | Max recommended: {{ item.recommended_max }}
              </template>
            </p>
          </div>
          <select
            v-model="overOptDecisions[item.keyword]"
            class="text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-800">
            <option value="keep">
              Keep
            </option>
            <option value="cap">
              {{ item.recommended_max ? `Cap at ${item.recommended_max}` : 'Cap' }}
            </option>
            <option value="exclude">
              Exclude
            </option>
          </select>
        </div>
        <button @click="handleOverOptimization" class="btn btn-primary btn-sm mt-3">
          Apply Decisions
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import consumer from '~/channels/consumer'
import { runCounting, getKeywordOccurrences, handleOverOptimization as submitOverOptimization } from '~/javascript/api/keyword_occurrences.js'

const props = defineProps({
  pageId: { type: String, required: true }
})

const emit = defineEmits(['completed'])

let subscription = null
let pollTimer = null

const loading = ref(true)
const occurrence = ref(null)
const progressMessage = ref('')
const overOptDecisions = ref({})
const expandedSections = ref({
  headings: true,
  body: true,
  gaps: true,
  tfidf: false,
  master: false,
  verification: true
})

const headingDeltas = computed(() => occurrence.value?.heading_deltas || [])
const bodyDeltas = computed(() => occurrence.value?.body_deltas || [])
const placementGaps = computed(() => occurrence.value?.placement_gaps || [])
const tfidfNotes = computed(() => occurrence.value?.tfidf_notes || [])
const masterTable = computed(() => occurrence.value?.master_table || [])
const verificationChecklist = computed(() => occurrence.value?.verification_checklist || [])
const overOptimized = computed(() => occurrence.value?.over_optimized || [])
const competitorNames = computed(() => occurrence.value?.competitor_names || [])

const criticalCount = computed(() => headingDeltas.value.filter((d) => d.priority === 'critical').length)
const gapCount = computed(() => placementGaps.value.length)
const tfidfNoteCount = computed(() => tfidfNotes.value.length)

const priorityBadgeClass = (priority) => {
  const map = {
    critical: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    high: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    normal: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
  }
  return map[priority] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
}

const statusBadgeClass = (status) => {
  const map = {
    ON_TARGET: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    UNDER: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    OVER: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
  }
  return map[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
}

const h1StatusClass = (status) => {
  const map = {
    CRITICAL_MISSING: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    SHOULD_ADD: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    present: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
  }
  return map[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
}

const deltaTextClass = (delta) => {
  if (delta > 0) return 'text-red-600 dark:text-red-400'
  if (delta < 0) return 'text-amber-600 dark:text-amber-400'
  return 'text-green-600 dark:text-green-400'
}

const fetchOccurrences = async() => {
  try {
    loading.value = true
    const response = await getKeywordOccurrences(props.pageId)
    occurrence.value = response.data.keyword_occurrence
    if (occurrence.value?.status === 'completed') {
      emit('completed')
    }
    // Initialize over-optimization decisions
    if (occurrence.value?.over_optimized) {
      occurrence.value.over_optimized.forEach((item) => {
        if (!overOptDecisions.value[item.keyword]) {
          overOptDecisions.value[item.keyword] = 'keep'
        }
      })
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error fetching occurrences:', e)
  } finally {
    loading.value = false
  }
}

const subscribeToChannel = () => {
  if (subscription) subscription.unsubscribe()
  subscription = consumer.subscriptions.create(
    { channel: 'KeywordOccurrenceChannel', page_id: props.pageId },
    {
      received(data) {
        progressMessage.value = data.message || ''
        if (data.step === 'completed' || data.step === 'failed') {
          stopPolling()
          fetchOccurrences()
        }
      }
    }
  )
}

const handleRun = async() => {
  try {
    await runCounting(props.pageId)
    subscribeToChannel()
    occurrence.value = { status: 'counting' }
    loading.value = false
    startPolling()
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to start counting:', e)
  }
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(async () => {
    try {
      const response = await getKeywordOccurrences(props.pageId)
      const o = response.data.keyword_occurrence
      if (o && (o.status === 'completed' || o.status === 'failed')) {
        occurrence.value = o
        if (o.status === 'completed') emit('completed')
        stopPolling()
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const handleOverOptimization = async() => {
  try {
    const response = await submitOverOptimization(props.pageId, overOptDecisions.value)
    occurrence.value = response.data.keyword_occurrence
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to handle over-optimization:', e)
  }
}

onMounted(async() => {
  await fetchOccurrences()
  subscribeToChannel()
  // Auto-run if no occurrence exists
  if (!occurrence.value) {
    handleRun()
  } else if (occurrence.value.status !== 'completed' && occurrence.value.status !== 'failed') {
    startPolling()
  }
})

onUnmounted(() => {
  subscription?.unsubscribe()
  stopPolling()
})
</script>
